import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {
    Box,
    Button,
    Image,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage} from 'react-intl'
import {getUrl} from '../../../utils/cloudinary-utils'
import {useAddToCartDrawerContext} from '../../../hooks/use-add-to-cart-drawer'
import {useSiteContext} from '../../../contexts/site'
import useMonetate from '../../../hooks/use-monetate'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import useTagManager from '../../../hooks/use-tag-manager'
import {useGlobaleContext} from '../../../contexts/globale'

const LARGE = 'large'

const QuickAddChild = ({
    product = {},
    addToCart,
    selectedVariant: variant,
    showAddedCtaForParent,
    customer,
    wishlist,
    variationParams,
    priceData,
    isBasketLoading = false
}) => {
    const location = useLocation()
    const monetate = useMonetate()
    const tagManager = useTagManager()
    const {globaleCurrency} = useGlobaleContext()
    const styles = useMultiStyleConfig('ProductTileStyles')
    const {setOpenBonusModal} = useSiteContext()
    const {onOpen: onAddToCartDrawerOpen, onClose: onAddToCartDrawerClose} =
        useAddToCartDrawerContext()

    // Styles for out-of-stock state
    const oosStyles = !product?.orderable
        ? {
              backgroundColor: 'gray.40',
              border: '0px',
              color: 'gray.650'
          }
        : {}

    const [showAddToCartLoader, setShowAddToCartLoader] = useState(false)
    const [showAddedCta, setShowAddedCta] = useState(false)

    const quantity = 1 // Hardcoded quantity

    const addProductToBasket = async (e) => {
        e.preventDefault()
        setShowAddToCartLoader(true)
        try {
            const response = await addToCart(variant, quantity, null, null, null, true)
            const {productSelectionValues: itemsAdded, basketResponse} = response || {}

            setShowAddToCartLoader(false)

            if (itemsAdded?.length) {
                setShowAddedCta(true)
                const heroImageGroup = findImageGroupBy(product?.imageGroups, {
                    viewType: LARGE,
                    selectedVariationAttributes: variationParams
                })

                onAddToCartDrawerOpen({
                    product,
                    itemsAdded,
                    selectedQuantity: quantity,
                    priceData,
                    heroImage: heroImageGroup?.images?.[0]?.disBaseLink
                })

                setOpenBonusModal(true)

                monetate.monetateTrackPage(
                    'addToBagModal',
                    window.mtBreadcrumb,
                    basketResponse?.productItems,
                    customer?.customerType === 'registered',
                    wishlist?.customerProductListItems?.length || 0,
                    null,
                    null,
                    itemsAdded,
                    basketResponse?.giftCertificateItems
                )
                tagManager.addToCart(product, variant, null, null, 'pdp')
                tagManager.addToCartGA4({
                    itemsAdded: [{product: product?.representedProduct || product}],
                    variant,
                    method: 'pdp',
                    currency: globaleCurrency
                })
                setTimeout(() => setShowAddedCta(false), 3000)
            }
        } catch (e) {
            console.warn('Child product quick add error: ', e.message)
            setShowAddToCartLoader(false)
        }
    }

    // Close add-to-cart drawer on route change
    useEffect(() => {
        onAddToCartDrawerClose()
    }, [location?.pathname])

    return (
        <Box
            className="quick-add-child-wrapper"
            position="absolute"
            left={{base: '7px', lg: '50%'}}
            bottom={{base: '7px', lg: '0px'}}
            transform={{base: 'unset', lg: 'translateX(-50%)'}}
        >
            <Button
                alignItems="center"
                justifyContent="center"
                className="add-only-btn"
                isDisabled={product?.productId && !product?.orderable}
                isLoading={showAddToCartLoader || isBasketLoading}
                onClick={addProductToBasket}
                {...styles.quickAdd.button}
                {...oosStyles}
                display={{base: 'none', lg: product?.productId ? 'flex' : 'none'}}
            >
                {showAddedCta ||
                    (showAddedCtaForParent && (
                        <Image
                            src={getUrl('UIImages/images/tick-circle-white.svg')}
                            className="tick-icon"
                            height="14px"
                            width="14px"
                            marginRight="5px"
                            filter="invert(1)"
                            alt="Added to Bag"
                        />
                    ))}

                {showAddedCta || showAddedCtaForParent ? (
                    <FormattedMessage
                        defaultMessage="Added"
                        id="product_view.button.added_to_cart"
                    />
                ) : product?.orderable ? (
                    <FormattedMessage defaultMessage="Add to Bag" id="add.to.bag" />
                ) : (
                    <FormattedMessage defaultMessage="Sold Out" id="sold.out" />
                )}
            </Button>
            <Box
                isDisabled={product?.productId && !product?.orderable}
                onClick={addProductToBasket}
                display={{base: product?.productId ? 'flex' : 'none', lg: 'none'}}
            >
                {showAddedCta || showAddedCtaForParent ? (
                    <Image
                        src={getUrl('UIImages/images/icon-tick-circle.svg')}
                        className="tick-icon"
                        height="15px"
                        width="15px"
                        alt="Added to Bag"
                    />
                ) : product?.orderable ? (
                    <Image
                        src={getUrl('UIImages/images/plus-circle.svg')}
                        className="tick-icon"
                        height="15px"
                        width="15px"
                        alt="Added to Bag"
                    />
                ) : (
                    <Image
                        src={getUrl('UIImages/images/icon-crossed-plus.svg')}
                        className="tick-icon"
                        height="15px"
                        width="15px"
                        alt="Added to Bag"
                    />
                )}
            </Box>
        </Box>
    )
}

QuickAddChild.propTypes = {
    product: PropTypes.object,
    addToCart: PropTypes.func,
    selectedVariant: PropTypes.any,
    customer: PropTypes.object,
    wishlist: PropTypes.object,
    priceData: PropTypes.object,
    variationParams: PropTypes.any,
    isBasketLoading: PropTypes.bool,
    showAddedCtaForParent: PropTypes.bool
}

export default QuickAddChild
